export const LOCALES = ["en", "pt-BR"] as const
export type Locale = (typeof LOCALES)[number]
export const DEFAULT_LOCALE: Locale = "en"
export const LOCALE_STORAGE_KEY = "formatloom-locale"

const ENGLISH_COPY = {
  "brand.name": "Formatloom",
  "brand.subtitle": "Structured data, locally",
  "brand.home_aria": "Formatloom home",
  "brand.mark_alt": "Mirtilo mark",
  "nav.tools": "Workbench tools",
  "nav.format_json": "Format JSON",
  "nav.validate_json": "Validate JSON",
  "nav.json_yaml": "JSON ↔ YAML",
  "preferences.theme": "Theme",
  "preferences.language": "Language",
  "preferences.controls": "Preferences",
  "theme.system": "System",
  "theme.light": "Light",
  "theme.dark": "Dark",
  "locale.en.short": "EN",
  "locale.pt_br.short": "PT",
  "locale.en.full": "English",
  "locale.pt_br.full": "Português (Brasil)",
  "routes.formatter.label": "JSON Formatter",
  "routes.formatter.title": "JSON Formatter · Formatloom",
  "routes.validator.label": "JSON Validator",
  "routes.validator.title": "JSON Validator · Formatloom",
  "routes.yaml-json.label": "JSON ↔ YAML",
  "routes.yaml-json.title": "JSON ↔ YAML · Formatloom",
  "page.eyebrow": "Browser-local workbench",
  "page.lede": "Inspect, format, validate, and translate structured data without sending your document to a server.",
  "page.privacy_badge": "No account · No database · No uploads",
  "workbench.heading_suffix": "workbench",
  "operation.choose": "Choose an operation",
  "operation.group": "Operation",
  "operation.format_json": "Format JSON",
  "operation.validate_json": "Validate JSON",
  "operation.json_to_yaml": "JSON → YAML",
  "operation.yaml_to_json": "YAML → JSON",
  "output.label": "JSON output",
  "output.pretty": "Readable (pretty)",
  "output.compact": "Compact (minified)",
  "selected": "selected",
  "input.heading": "Input",
  "input.hint_json": "Strict JSON · comments and trailing commas are rejected",
  "input.hint_yaml": "Safe YAML · one document · custom tags are rejected",
  "input.label": "Structured data input",
  "input.placeholder": "Paste JSON or YAML here…",
  "input.privacy": "Your input stays in this browser tab.",
  "result.heading": "Result",
  "result.hint": "Generated locally; nothing is saved by Formatloom.",
  "result.meta": "Output",
  "result.label": "Structured data result",
  "result.valid": "Valid JSON. The source was checked locally and was not changed.",
  "action.sample": "Use sample",
  "action.run": "Run locally",
  "action.copy": "Copy",
  "action.download": "Download",
  "action.reset": "Reset",
  "status.empty": "Ready for input.",
  "status.editing": "Input changed. Run the selected operation when ready.",
  "status.processing": "Processing locally in your browser…",
  "status.success": "Operation completed locally.",
  "status.warning": "Operation completed with a fidelity warning.",
  "status.error": "The operation needs attention before it can complete.",
  "limit": "Limit: 1 MB · JSON and YAML only",
  "privacy.label": "Formatloom product boundaries",
  "privacy.local.title": "Local by default",
  "privacy.local.body": "Parsing, formatting, validation, and conversion run in a browser worker. The application never receives your document.",
  "privacy.limit.title": "Clear limitations",
  "privacy.limit.body": "Strict JSON rejects comments and trailing commas. YAML conversion accepts one safe document and calls out fidelity warnings.",
  "privacy.analytics.title": "Outcome-only analytics",
  "privacy.analytics.body": "Optional Umami telemetry records tool outcomes and formats only. It does not receive input, output, hashes, or content-derived values.",
  "footer.tagline": "Structured data, locally.",
  "footer.copyright": "© {year} Mirtilo IO",
  "footer.location": "Independent · Portugal · Remote",
  "result.copy_success": "Result copied to the clipboard.",
  "location": " Line {line}, column {column}.",
  "diagnostic.empty-input": "Add JSON or YAML text before processing.",
  "diagnostic.worker-unavailable": "The browser worker is unavailable. Reload the page and try again.",
  "diagnostic.worker-error": "The browser worker stopped unexpectedly. Reload and try again.",
  "diagnostic.invalid-operation": "Choose a supported workbench operation before processing.",
  "diagnostic.invalid-output-style": "Choose readable or compact JSON output before processing.",
  "diagnostic.input-too-large": "Keep the input at or below 1 MB to keep browser-local processing responsive.",
  "diagnostic.processing-failed": "The browser could not complete this operation. Check the input and try again.",
  "diagnostic.json-trailing-comma": "JSON values must not have a trailing comma.",
  "diagnostic.json-comments-not-allowed": "Comments are not valid in strict JSON. Remove the comment and try again.",
  "diagnostic.json-invalid-number": "The number is not valid JSON.",
  "diagnostic.json-property-expected": "An object property name is required here.",
  "diagnostic.json-colon-expected": "Add a colon between the property name and its value.",
  "diagnostic.json-object-not-closed": "Close the JSON object with a right brace.",
  "diagnostic.json-array-not-closed": "Close the JSON array with a right bracket.",
  "diagnostic.json-string-not-closed": "Close the string before the end of the document.",
  "diagnostic.json-parse-error": "The input is not valid strict JSON. Review the indicated location.",
  "diagnostic.yaml-parse-error": "The input is not valid YAML. Review the indicated location.",
  "diagnostic.yaml-multi-document": "Only one YAML document can be converted at a time.",
  "diagnostic.yaml-custom-tag": "Custom and explicit YAML tags are not supported for safe conversion.",
  "diagnostic.yaml-unsupported-construct": "This YAML value cannot be represented safely as JSON.",
  "warning.duplicate-key.json": "The object contains a duplicate key. JSON parsers may keep only the last value.",
  "warning.duplicate-key.yaml": "The YAML mapping contains a duplicate key. JSON output keeps one value.",
  "warning.unsafe-number": "This number may lose precision in browser JavaScript processing.",
  "warning.yaml-tag-rejected": "Explicit YAML tags are not supported by the safe JSON converter.",
  "warning.yaml-comment-loss": "YAML comments are not included in JSON output.",
  "warning.yaml-anchor-loss": "YAML anchors are expanded during JSON conversion and cannot be preserved.",
  "warning.yaml-alias-loss": "YAML aliases are expanded during JSON conversion and cannot be preserved.",
  "warning.yaml-order-loss": "YAML mapping key order changes in JSON output and cannot be preserved.",
  "warning.yaml-parser-warning": "The YAML parser reported a non-fatal issue. Review the result before using it.",
} as const

const PORTUGUESE_COPY: Record<keyof typeof ENGLISH_COPY, string> = {
  "brand.name": "Formatloom",
  "brand.subtitle": "Dados estruturados, localmente",
  "brand.home_aria": "Página inicial do Formatloom",
  "brand.mark_alt": "Marca Mirtilo",
  "nav.tools": "Ferramentas de trabalho",
  "nav.format_json": "Formatar JSON",
  "nav.validate_json": "Validar JSON",
  "nav.json_yaml": "JSON ↔ YAML",
  "preferences.theme": "Tema",
  "preferences.language": "Idioma",
  "preferences.controls": "Preferências",
  "theme.system": "Sistema",
  "theme.light": "Claro",
  "theme.dark": "Escuro",
  "locale.en.short": "EN",
  "locale.pt_br.short": "PT",
  "locale.en.full": "Inglês",
  "locale.pt_br.full": "Português (Brasil)",
  "routes.formatter.label": "Formatador JSON",
  "routes.formatter.title": "Formatador JSON · Formatloom",
  "routes.validator.label": "Validador JSON",
  "routes.validator.title": "Validador JSON · Formatloom",
  "routes.yaml-json.label": "JSON ↔ YAML",
  "routes.yaml-json.title": "JSON ↔ YAML · Formatloom",
  "page.eyebrow": "Área de trabalho local do navegador",
  "page.lede": "Inspecione, formate, valide e traduza dados estruturados sem enviar seu documento para um servidor.",
  "page.privacy_badge": "Sem conta · Sem banco de dados · Sem uploads",
  "workbench.heading_suffix": "área de trabalho",
  "operation.choose": "Escolha uma operação",
  "operation.group": "Operação",
  "operation.format_json": "Formatar JSON",
  "operation.validate_json": "Validar JSON",
  "operation.json_to_yaml": "JSON → YAML",
  "operation.yaml_to_json": "YAML → JSON",
  "output.label": "saída JSON",
  "output.pretty": "Legível (formatado)",
  "output.compact": "Compacto (minificado)",
  "selected": "selecionado",
  "input.heading": "Entrada",
  "input.hint_json": "JSON estrito · comentários e vírgulas finais são rejeitados",
  "input.hint_yaml": "YAML seguro · um documento · tags personalizadas são rejeitadas",
  "input.label": "Entrada de dados estruturados",
  "input.placeholder": "Cole JSON ou YAML aqui…",
  "input.privacy": "Sua entrada permanece nesta aba do navegador.",
  "result.heading": "Resultado",
  "result.hint": "Gerado localmente; nada é salvo pelo Formatloom.",
  "result.meta": "Saída",
  "result.label": "Resultado de dados estruturados",
  "result.valid": "JSON válido. A fonte foi verificada localmente e não foi alterada.",
  "action.sample": "Usar exemplo",
  "action.run": "Executar localmente",
  "action.copy": "Copiar",
  "action.download": "Baixar",
  "action.reset": "Redefinir",
  "status.empty": "Pronto para receber entrada.",
  "status.editing": "A entrada mudou. Execute a operação selecionada quando estiver pronto.",
  "status.processing": "Processando localmente no seu navegador…",
  "status.success": "Operação concluída localmente.",
  "status.warning": "Operação concluída com um aviso de fidelidade.",
  "status.error": "A operação precisa de atenção antes de ser concluída.",
  "limit": "Limite: 1 MB · somente JSON e YAML",
  "privacy.label": "Limites do produto Formatloom",
  "privacy.local.title": "Local por padrão",
  "privacy.local.body": "A análise, formatação, validação e conversão são executadas em um worker do navegador. O aplicativo nunca recebe seu documento.",
  "privacy.limit.title": "Limitações claras",
  "privacy.limit.body": "O JSON estrito rejeita comentários e vírgulas finais. A conversão YAML aceita um documento seguro e sinaliza avisos de fidelidade.",
  "privacy.analytics.title": "Análises somente de resultados",
  "privacy.analytics.body": "A telemetria opcional do Umami registra apenas resultados e formatos das ferramentas. Ela não recebe entrada, saída, hashes ou valores derivados do conteúdo.",
  "footer.tagline": "Dados estruturados, localmente.",
  "footer.copyright": "© {year} Mirtilo IO",
  "footer.location": "Independente · Portugal · Remoto",
  "result.copy_success": "Resultado copiado para a área de transferência.",
  "location": " Linha {line}, coluna {column}.",
  "diagnostic.empty-input": "Adicione texto JSON ou YAML antes de processar.",
  "diagnostic.worker-unavailable": "O worker do navegador está indisponível. Recarregue a página e tente novamente.",
  "diagnostic.worker-error": "O worker do navegador parou inesperadamente. Recarregue a página e tente novamente.",
  "diagnostic.invalid-operation": "Escolha uma operação compatível antes de processar.",
  "diagnostic.invalid-output-style": "Escolha uma saída JSON legível ou compacta antes de processar.",
  "diagnostic.input-too-large": "Mantenha a entrada em até 1 MB para preservar a agilidade do processamento local.",
  "diagnostic.processing-failed": "O navegador não conseguiu concluir esta operação. Verifique a entrada e tente novamente.",
  "diagnostic.json-trailing-comma": "Os valores JSON não podem terminar com vírgula.",
  "diagnostic.json-comments-not-allowed": "Comentários não são válidos em JSON estrito. Remova o comentário e tente novamente.",
  "diagnostic.json-invalid-number": "O número não é um JSON válido.",
  "diagnostic.json-property-expected": "O nome de uma propriedade do objeto é necessário aqui.",
  "diagnostic.json-colon-expected": "Adicione dois-pontos entre o nome da propriedade e seu valor.",
  "diagnostic.json-object-not-closed": "Feche o objeto JSON com uma chave direita.",
  "diagnostic.json-array-not-closed": "Feche o array JSON com um colchete direito.",
  "diagnostic.json-string-not-closed": "Feche a string antes do fim do documento.",
  "diagnostic.json-parse-error": "A entrada não é um JSON estrito válido. Revise o local indicado.",
  "diagnostic.yaml-parse-error": "A entrada não é um YAML válido. Revise o local indicado.",
  "diagnostic.yaml-multi-document": "Somente um documento YAML pode ser convertido por vez.",
  "diagnostic.yaml-custom-tag": "Tags YAML personalizadas e explícitas não são compatíveis com a conversão segura.",
  "diagnostic.yaml-unsupported-construct": "Este valor YAML não pode ser representado com segurança como JSON.",
  "warning.duplicate-key.json": "O objeto contém uma chave duplicada. Os analisadores JSON podem manter apenas o último valor.",
  "warning.duplicate-key.yaml": "O mapeamento YAML contém uma chave duplicada. A saída JSON mantém um valor.",
  "warning.unsafe-number": "Este número pode perder precisão durante o processamento JavaScript no navegador.",
  "warning.yaml-tag-rejected": "Tags YAML explícitas não são compatíveis com o conversor JSON seguro.",
  "warning.yaml-comment-loss": "Comentários YAML não são incluídos na saída JSON.",
  "warning.yaml-anchor-loss": "Âncoras YAML são expandidas durante a conversão para JSON e não podem ser preservadas.",
  "warning.yaml-alias-loss": "Aliases YAML são expandidos durante a conversão para JSON e não podem ser preservados.",
  "warning.yaml-order-loss": "A ordem das chaves do mapeamento YAML muda na saída JSON e não pode ser preservada.",
  "warning.yaml-parser-warning": "O analisador YAML relatou um problema não fatal. Revise o resultado antes de usá-lo.",
}

export const CATALOG = { en: ENGLISH_COPY, "pt-BR": PORTUGUESE_COPY } as const
export type CopyKey = keyof typeof ENGLISH_COPY

export function normalizeLocale(value: unknown): Locale {
  return typeof value === "string" && LOCALES.includes(value as Locale)
    ? (value as Locale)
    : DEFAULT_LOCALE
}

export function readStoredLocale(storage: Storage | null | undefined): Locale {
  try {
    return normalizeLocale(storage?.getItem(LOCALE_STORAGE_KEY))
  } catch {
    return DEFAULT_LOCALE
  }
}

export function persistLocale(
  storage: Storage | null | undefined,
  locale: Locale,
): void {
  try {
    storage?.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Browser privacy settings can make localStorage unavailable.
  }
}

export function translate(
  locale: Locale,
  key: string,
  variables: Record<string, string | number> = {},
): string {
  const fallback = ENGLISH_COPY[key as CopyKey]
  const value = CATALOG[locale][key as CopyKey] ?? fallback ?? key
  return value.replace(/\{(\w+)\}/g, (_, variable: string) =>
    Object.prototype.hasOwnProperty.call(variables, variable)
      ? String(variables[variable])
      : `{${variable}}`,
  )
}

export function currentLocale(documentObject: Document): Locale {
  return normalizeLocale(documentObject.documentElement.dataset.locale)
}

function localizeElements(documentObject: Document, locale: Locale): void {
  documentObject.querySelectorAll<HTMLElement>("[data-copy-key]").forEach((element) => {
    const variables: Record<string, string | number> = {}
    if (element.dataset.copyYear) variables.year = element.dataset.copyYear
    element.textContent = translate(locale, element.dataset.copyKey ?? "", variables)
  })

  documentObject.querySelectorAll<HTMLElement>("[data-copy-placeholder]").forEach((element) => {
    element.setAttribute("placeholder", translate(locale, element.dataset.copyPlaceholder ?? ""))
  })

  documentObject.querySelectorAll<HTMLElement>("[data-copy-aria-label]").forEach((element) => {
    element.setAttribute("aria-label", translate(locale, element.dataset.copyAriaLabel ?? ""))
  })

  documentObject.querySelectorAll<HTMLElement>("[data-copy-title]").forEach((element) => {
    element.setAttribute("title", translate(locale, element.dataset.copyTitle ?? ""))
  })

  documentObject.querySelectorAll<HTMLImageElement>("[data-copy-alt]").forEach((element) => {
    element.alt = translate(locale, element.dataset.copyAlt ?? "")
  })
}

type LocalizationWindow = Pick<Window, "localStorage">

function browserStorage(windowObject: LocalizationWindow): Storage | null {
  try {
    return windowObject.localStorage
  } catch {
    return null
  }
}

export function applyLocalization(documentObject: Document, locale: Locale): void {
  documentObject.documentElement.lang = locale
  documentObject.documentElement.dataset.locale = locale
  localizeElements(documentObject, locale)
  documentObject.querySelectorAll<HTMLElement>("[data-locale]").forEach((button) => {
    const active = button.dataset.locale === locale
    button.setAttribute("aria-pressed", String(active))
    button.classList.toggle("is-active", active)
  })
}

export function initializeLocalization(
  documentObject: Document,
  windowObject: LocalizationWindow,
): () => void {
  const storage = browserStorage(windowObject)
  let locale = readStoredLocale(storage)

  const render = (): void => applyLocalization(documentObject, locale)
  const handleClick = (event: Event): void => {
    const button = (event.target as HTMLElement).closest<HTMLElement>("[data-locale]")
    if (!button) return

    locale = normalizeLocale(button.dataset.locale)
    persistLocale(storage, locale)
    render()
    documentObject.dispatchEvent(new CustomEvent("formatloom:locale-change", { detail: { locale } }))
  }

  documentObject.addEventListener("click", handleClick)
  render()

  return () => documentObject.removeEventListener("click", handleClick)
}
